export default function ArchitectureSlide() {
  return (
    <div className="w-full max-w-4xl mx-auto relative z-10 flex flex-col items-center px-4">
      <h2 className="font-display text-6xl mb-6 text-center">How It Works</h2>
      <p className="text-gray-400 text-xl text-center mb-14 max-w-3xl">
        Users talk to Miles, Miles knows how to use all DeFi tools <span className="inline-block w-2 h-2 rounded-full bg-avo-green mx-2 align-middle" /> better overall UX.
      </p>

      {/* Horizontal flow */}
      <div className="w-full flex items-stretch justify-center gap-0">
        {/* User box */}
        <div className="flex-shrink-0 w-32 flex">
          <div className="bg-[#1a1a1e] border border-gray-800 rounded-2xl px-4 py-6 text-center flex flex-col items-center justify-center w-full">
            <h3 className="text-xl font-bold text-white mb-2">User</h3>
            <p className="text-gray-400 text-sm">Talk to Miles</p>
          </div>
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 flex items-center px-2">
          <div className="w-6 h-px bg-gradient-to-r from-gray-700 to-avo-teal/50" />
          <svg className="w-3.5 h-3.5 text-avo-teal/60 -ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        {/* Avo Infra container */}
        <div className="min-w-0 flex-1 border border-dashed border-gray-700 rounded-2xl p-4 relative">
          <div className="absolute -top-3 left-6 bg-black px-3 text-sm font-pixel text-gray-500 tracking-widest">
            AVO INFRA
          </div>

          <div className="flex items-stretch gap-3 h-full">
            {/* Miles box - full height */}
            <div className="flex-shrink-0 w-40 bg-gradient-to-b from-[#0f1a1e] to-[#0d0d14] border border-avo-teal/30 rounded-xl px-4 text-center shadow-[0_0_20px_rgba(0,205,183,0.08)] flex flex-col items-center justify-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 rounded-full bg-avo-teal" />
                <h3 className="text-xl font-display text-avo-cyan">Miles</h3>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed text-left">Reason<br />Evaluate<br />Execute</p>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0 flex items-center">
              <div className="w-4 h-px bg-gradient-to-r from-avo-teal/40 to-gray-700" />
              <svg className="w-3 h-3 text-gray-600 -ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Three equal sub-boxes */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="bg-[#1a1a1e] border border-gray-800 rounded-xl px-4 py-4 text-center flex-1 flex flex-col items-center justify-center">
                <h4 className="text-base font-bold text-white mb-1">Marketplace</h4>
                <p className="text-xs text-gray-400 italic">"Find the top performing index over the past 3 months and allocate $200"</p>
              </div>
              <div className="bg-[#1a1a1e] border border-gray-800 rounded-xl px-4 py-4 text-center flex-1 flex flex-col items-center justify-center">
                <h4 className="text-base font-bold text-white mb-1">Direct Swaps</h4>
                <p className="text-xs text-gray-400 italic">"Swap my SOL to USDC and send it to 7GKp..."</p>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <div className="bg-[#1a1a1e] border border-gray-800 rounded-xl px-4 py-3 text-center flex flex-col items-center justify-center">
                  <h4 className="text-base font-bold text-white mb-1">Monitoring</h4>
                  <p className="text-xs text-gray-400 italic">"When SOL hits $60, DCA $100 every hour"</p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="bg-[#1a1a1e] border border-gray-800 rounded-xl px-4 py-3 text-center">
                    <h4 className="text-sm font-bold text-white mb-1">LP Positioning</h4>
                    <p className="text-xs text-gray-400 italic">"LP half our holdings in the USDC/SOL pool on Meteora"</p>
                  </div>
                  <div className="bg-[#1a1a1e] border border-gray-800 rounded-xl px-4 py-3 text-center">
                    <h4 className="text-sm font-bold text-white mb-1">Lending</h4>
                    <p className="text-xs text-gray-400 italic">"Lend my USDC on Kamino at the best rate"</p>
                  </div>
                  <div className="bg-[#1a1a1e] border border-avo-purple/40 rounded-xl px-4 py-3 text-center">
                    <h4 className="text-sm font-bold text-white mb-1">Rest of DeFi</h4>
                    <p className="text-xs text-gray-400 italic">"What's the best yield for stables right now?"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 flex items-center px-2">
          <div className="w-6 h-px bg-gradient-to-r from-gray-700 to-avo-lime/50" />
          <svg className="w-3.5 h-3.5 text-avo-lime/60 -ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        {/* Solana box */}
        <div className="flex-shrink-0 w-28 flex">
          <div className="bg-gradient-to-b from-[#0d140d] to-[#0d0d14] border border-avo-lime/20 rounded-2xl px-3 py-6 text-center shadow-[0_0_20px_rgba(158,255,0,0.05)] flex flex-col items-center justify-center w-full">
            <h3 className="text-xl font-bold text-white">Solana</h3>
          </div>
        </div>
      </div>
    </div>
  );
}
